import LoopIcon from '@mui/icons-material/Loop';
import { Step } from '../../../../../components/Step';
import Grid2 from '@mui/material/Unstable_Grid2';
import { Checkboxes } from 'mui-rff';
import { Table } from 'src/shared/components/Table/Table';
import { useFormState } from 'react-final-form';
import { type DataTableRow, useColumnsV2 } from '../../../../../utils/constants';
import { useTranslation } from 'react-i18next';
import {
  type FactorialCustomFieldsResponse,
  useGetFactorialCustomFieldsQuery
} from 'src/shared/api/integrations/factorial';
import { ShowLoading } from 'src/shared/components/ShowLoading';
import { ShowError } from 'src/shared/components/ShowError';

export enum COLUMN_FIELD_NAMES {
  FIRST_NAME = 'firstName',
  DOCUMENT_NUMBER = 'documentNumber',
  PAYROLL_CODE = 'payrollCode',
  CONTRACT_PHONE = 'phone',
  GROSS_SALARY = 'grossSalary',
  EMAIL = 'email'
}

export const columnFieldLabel = {
  [COLUMN_FIELD_NAMES.FIRST_NAME]: 'table.first name',
  [COLUMN_FIELD_NAMES.DOCUMENT_NUMBER]: 'table.document number',
  [COLUMN_FIELD_NAMES.PAYROLL_CODE]: 'table.payroll id',
  [COLUMN_FIELD_NAMES.EMAIL]: 'table.email',
  [COLUMN_FIELD_NAMES.CONTRACT_PHONE]: 'table.mobile',
  [COLUMN_FIELD_NAMES.GROSS_SALARY]: 'table.monthly gross remuneration'
};

type CustomFieldsTableProps = {
  data: FactorialCustomFieldsResponse | undefined;
};

/**
 * Custom fields can be numerous and slow to load, so they are only fetched when the checkbox is enabled
 */
export const CustomFieldsTable = ({ data }: CustomFieldsTableProps) => {
  const dataTable: DataTableRow[] = Object.values(COLUMN_FIELD_NAMES).map((fieldName: COLUMN_FIELD_NAMES) => ({
    fieldInPayflow: {
      label: columnFieldLabel[fieldName],
      value: fieldName
    }
  }));
  const columns = useColumnsV2('Factorial', data ?? []);

  return <Table rows={dataTable} columns={columns} />;
};

type CustomFieldsFormProps = {
  data: Record<string, string>;
};

export const CustomFieldsForm = ({ data }: CustomFieldsFormProps) => {
  const {
    data: customFields,
    isLoading,
    isError,
    error
  } = useGetFactorialCustomFieldsQuery({ subsidiary: data.company }, { skip: !data?.withCustomFields });

  if (isLoading) {
    return <ShowLoading />;
  }

  if (isError) {
    return <ShowError error={error} />;
  }

  return <CustomFieldsTable data={customFields} />;
};

export const Step5 = () => {
  const { t } = useTranslation('settings');
  const { values } = useFormState();

  return (
    <Step
      title={`${t('integrations.step')} 5`}
      subtitle={`${t('If you use custom fields', { softwareName: 'factorial' })}:`}
      Icon={LoopIcon}
    >
      <>
        <Grid2 xs={12}>
          <Checkboxes
            name="withCustomFields"
            data={{ label: t('use custom fields', { ns: 'settings' }), value: true }}
          />
        </Grid2>

        {values?.withCustomFields && <CustomFieldsForm data={values} />}
      </>
    </Step>
  );
};
